import React, { createContext, useState } from "react";
import {
    addLineItem,
    applyDiscountCode,
    getCheckout,
    getOrCreateCheckoutId,
    removeDiscountCode,
    updateLineItem,
    applyProxyDiscountV2, 
} from "services/storefront/cart";
import { getProducts } from "services/storefront/common";
import axios from "axios";
import getStableCheckout from "helpers/checkout-build-function";
import { checkoutProxyURL, SYNC_WITH_OLD_SITE } from "utils/constants";
import { createOrUpdateCartCookie, getCartSessionFromCookie } from "helpers/cart-sync";

const defaultPrice = { currencyCode: "USD", amount: 0 };

const initialState = {
    items: [],
    error: "",
    checkoutUrl: "",
    discountCode: "",
    totalPrice: defaultPrice,
    subtotalPrice: defaultPrice,
    discountPrice: defaultPrice,
    createdAt: "",
    updatedAt: "",
    hasDiscount: "",
    recommendations: [],
    isLoading: true,
};

export const CartContext = createContext(null);

const useCart = () => {
    const [cart, setCart] = useState(initialState);

    const getRecommendations = async (lineItems) => {
        if (!lineItems?.edges?.length) return [];
        const lineItemsHandles = lineItems.edges.map((lineItem) => lineItem.node.variant.product.handle);

        // Creates a unique Array of Cart Recommendation Handles from metafields (sf_upsell_products) and filters if the same handle is already in the cart
        const recommendationHandles = Array.from(new Set(lineItems.edges
            .flatMap((cartItem) => cartItem.node.variant.product.metafields)
            .filter((metafield) => metafield?.namespace === "sf_upsell_products" && !lineItemsHandles.includes(metafield.value))
            .map((metafield) => metafield.value)));

        if (recommendationHandles.length === 0) return [];
            
        return getProducts(recommendationHandles);
    };

    // Should be used on any function that changes the Checkout Object
    const updateCart = async (checkout) => {
        if (!Object.keys(checkout || {}).length) return;
        const lineItems = checkout?.lineItems;
        
        // Cart State
        const createdAt = new Date(checkout?.createdAt);
        const updatedAt = new Date(checkout?.updatedAt);
        const items = lineItems?.edges?.length ? lineItems.edges : [];

        const calculatedPrice = items.reduce((acc, item) => {
            const itemTotalPrice = item?.node?.customAttributes.length > 0 && item?.node?.customAttributes.find((attr) => attr.key === "__basePrice") ? +item.node.customAttributes.filter((attr) => attr.key === "__basePrice")[0].value * item.node.quantity : +item.node.variant.price.amount * item.node.quantity;
            return acc + itemTotalPrice;
        }, 0);

        const newCart = await getStableCheckout({ items });

        const appliedDiscount = await applyProxyDiscountV2({ ...newCart, discountCode: checkout.discountApplications.edges.length > 0 ? checkout.discountApplications.edges[0].node.code : "" });
        
        const subtotalPrice = { currencyCode: checkout?.lineItemsSubtotalPrice.currencyCode, amount: calculatedPrice };
        const hasDiscount = Boolean(checkout.discountApplications.edges.length);  
        
        const checkoutUrl = checkout?.webUrl;
        const totalPrice = { currencyCode: checkout?.totalPrice.currencyCode, amount: appliedDiscount?.total_discounts !== 0 ? calculatedPrice - +appliedDiscount.total_discounts : calculatedPrice };
        const discountCode = checkout?.discountApplications.edges.length > 0 ? checkout.discountApplications.edges[0].node.code : "";
        const discountPrice = { currencyCode: subtotalPrice.currencyCode, amount: appliedDiscount?.total_discounts !== 0 ? +appliedDiscount.total_discounts : subtotalPrice };
        const recommendations = await getRecommendations(lineItems);

        setCart(() => ({
            items,
            checkoutUrl,
            discountCode,
            totalPrice,
            subtotalPrice,
            discountPrice,
            createdAt,
            updatedAt,
            hasDiscount,
            recommendations,
            error: false,
            isLoading: false,
        }));

        if (SYNC_WITH_OLD_SITE) {
            await createOrUpdateCartCookie({
                items,
                error: false,
                checkoutUrl,
                discountCode,
                totalPrice,
                subtotalPrice,
                discountPrice,
                createdAt,
                updatedAt,
                recommendations,
                isLoading: false,
            });
        }
    };

    // Is called only when initially landing on the page
    const fetchCart = async () => {
        try {
            if (!SYNC_WITH_OLD_SITE) {
                const checkoutId = await getOrCreateCheckoutId();
                const checkout = await getCheckout(checkoutId);
                const lastUpdatedAt = new Date(checkout.data.node.updatedAt).getTime();

                // Should only be called if the cart has been updated or the first time landing in the website
                if (!cart.updatedAt || !(cart.updatedAt.getTime() === lastUpdatedAt)) await updateCart(checkout.data.node);
                return;
            }
            const cookieBase = getCartSessionFromCookie();

            const base = { 
                cartItemsLength: cookieBase === null ? 0 : cookieBase?.checkout?.checkout.items.length,
                fromHeadless: cookieBase === null ? true : cookieBase?.checkout.fromHeadless, 
                items: cookieBase === null ? [] : cookieBase?.checkout?.checkout.items,
            };

            if (base.cartItemsLength) {
                if (base.fromHeadless) {
                    const checkoutId = await getOrCreateCheckoutId();
                    const checkout = await getCheckout(checkoutId);
                    const lastUpdatedAt = new Date(checkout.data.node.updatedAt).getTime();
                
                    // Should only be called if the cart has been updated or the first time landing in the website
                    if (!cart.updatedAt || !(cart.updatedAt.getTime() === lastUpdatedAt)) await updateCart(checkout.data.node);
                } else {
                    const checkoutId = await getOrCreateCheckoutId(false, base.items);
                    const checkout = await getCheckout(checkoutId);
                    const lastUpdatedAt = new Date(checkout.data.node.updatedAt).getTime();
                    if (!cart.updatedAt || !(cart.updatedAt.getTime() === lastUpdatedAt)) await updateCart(checkout.data.node);
                }
            } else {
                // Creates a new checkout with a new checkoutId if one doesn't already exist
                const checkoutId = await getOrCreateCheckoutId(false, []);
                const checkout = await getCheckout(checkoutId);
                const lastUpdatedAt = new Date(checkout.data.node.updatedAt).getTime();
                
                // Should only be called if the cart has been updated or the first time landing in the website
                if (!cart.updatedAt || !(cart.updatedAt.getTime() === lastUpdatedAt)) await updateCart(checkout.data.node);
            }
        } catch (err) {
            setCart(async (prevState) => ({ ...prevState, error: err }));
        }
    };

    const addCartItem = async (items) => {
        try {
            const checkoutId = await getOrCreateCheckoutId();
            const lineItems = items.reduce((arr, item) => [...arr, {
                variantId: item.variantId,
                quantity: item?.quantity,
                customAttributes: item?.customAttributes ? item.customAttributes : [],
            }], []);
            const updatedCheckout = await addLineItem(checkoutId, lineItems);
            await updateCart(updatedCheckout.data.checkoutLineItemsAdd.checkout, "some");
        } catch (err) {
            setCart(async (prevState) => ({ ...prevState, error: err }));
        }
    };

    const updateCartItem = async (variantId, quantity) => {
        try {
            const checkoutId = await getOrCreateCheckoutId();
            const lineItem = [{
            // eslint-disable-next-line id-length
                id: variantId,
                quantity,
            }];

            const updatedCheckout = await updateLineItem(checkoutId, lineItem);

            await updateCart(updatedCheckout.data.checkoutLineItemsUpdate.checkout);
        } catch (err) {
            setCart(async (prevState) => ({ ...prevState, error: err }));
        }
    };

    const applyDiscount = async (discountCode) => {
        try {
            const checkoutId = await getOrCreateCheckoutId();
            const updatedCheckout = await applyDiscountCode(checkoutId, discountCode);
            const discountMessage = Boolean(updatedCheckout.data.checkoutDiscountCodeApplyV2.checkout.discountApplications.edges.length);

            if (!discountMessage) {
                setCart((prev) => ({ ...prev, hasDiscount: "Unable to find a valid discount matching the code entered" }));
            } else {
                await updateCart(updatedCheckout.data.checkoutDiscountCodeApplyV2.checkout);
            }
        } catch (err) {
            setCart(async (prevState) => ({ ...prevState, error: err }));
        }
    };

    const removeDiscount = async () => {
        try {
            const checkoutId = await getOrCreateCheckoutId();
            const updatedCheckout = await removeDiscountCode(checkoutId);
            await updateCart(updatedCheckout.data.checkoutDiscountCodeRemove.checkout);   
        } catch (err) {
            setCart(async (prevState) => ({ ...prevState, error: err }));
        }
    };
    
    const checkoutRedirect = async (inputRefs) => {
        const hasPatioProducts = cart.items.some((item) => item.node.customAttributes.some((attr) => attr.key === "_customProduct"));
        if (hasPatioProducts) {
            const checkoutObject = await getStableCheckout(cart, inputRefs);
            const proxyCheckout = await axios.post(checkoutProxyURL, checkoutObject)
                .catch((error) => {
                // eslint-disable-next-line no-console
                    console.log(error);
                });
            return proxyCheckout.data;
        } 
        return { checkout_link: cart.checkoutUrl };
    };

    return {
        cart, updateCartItem, addCartItem, fetchCart, applyDiscount, removeDiscount, checkoutRedirect,
    };
};

const CartProvider = ({ children }) => {
    const cart = useCart();
    return (
        <CartContext.Provider value={cart}>
            {children}
        </CartContext.Provider>
    );
};

export default CartProvider;
